import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { InsightsMonthlyRevenuePoint } from "@shared-core/data/api-types";
import {
  dedupCorrectionPct,
  firstMonthLabel,
  firstMonthMarginPct,
  formatGbpCompact,
  grossInflationMultiple,
  latestMonthMarginPct,
  latestMonthRevenue,
  niceAxisMax,
  periodGrossQuoted,
  periodQuotedValue,
  projectMonthlyRevenue,
  quotedRevenueMultiple,
  type MonthlyRevenueDatum,
  type RevenueBasis,
} from "@shared-core/domain/insights-monthly-revenue";

// Monthly revenue + contribution over the trailing window, with a
// booked/invoiced toggle AND the estimate/quoting overlay on a second (right)
// axis. Faithful to the settled design reference
// (docs/features/bi-native-analysis/charts/monthly-l24m.html): grouped bars for
// revenue and contribution per month; the Quoted value line (solid amber) and
// the Gross quoted line (dashed) on the right axis; a 4-KPI strip; and the
// partial final (MTD) month faded and excluded from the lines/totals.
//
// Server composes, client renders: this component only projects the fields the
// endpoint already returns (revenue_performance ⋈ quoted_value_monthly); it
// performs no aggregation.

const REVENUE_COLOR = "var(--color-chart-1)";
const CONTRIBUTION_COLOR = "var(--color-chart-2)";
// Amber for the quoting overlay, matching the mockup's --est / --gross.
const QUOTED_COLOR = "#b45309";
const GROSS_COLOR = "#d8a878";

export interface MonthlyRevenueChartProps {
  months: InsightsMonthlyRevenuePoint[];
  /** Initial basis; defaults to booked (the design's default). */
  defaultBasis?: RevenueBasis;
}

export function MonthlyRevenueChart({
  months,
  defaultBasis = "booked",
}: MonthlyRevenueChartProps) {
  const [basis, setBasis] = useState<RevenueBasis>(defaultBasis);
  const data = projectMonthlyRevenue(months, basis);

  if (months.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No revenue data for this period yet.
      </div>
    );
  }

  const basisWord = basis === "booked" ? "booked" : "invoiced";

  // The quoting lines drop the partial final (MTD) month — quotedValue/grossQuoted
  // are set to null on it so recharts breaks the line rather than diving.
  const chartData = data.map((d) => ({
    ...d,
    quotedLine: d.isPartial ? null : d.quotedValue,
    grossLine: d.isPartial ? null : d.grossQuoted,
  }));
  // Explicit 0-floored axis maxima so neither axis dips negative (see
  // niceAxisMax): left covers the revenue/contribution bars, right covers both
  // quoting lines — gross is the taller. Both use the same tick count, so their
  // gridlines stay aligned.
  const revenueMax = niceAxisMax(
    Math.max(0, ...data.map((d) => Math.max(d.revenue, d.contribution))),
  );
  const quotingMax = niceAxisMax(
    Math.max(0, ...data.map((d) => Math.max(d.quotedValue, d.grossQuoted))),
  );

  const latestRev = latestMonthRevenue(data);
  const latestMargin = latestMonthMarginPct(data);
  const firstMargin = firstMonthMarginPct(data);
  const firstLabel = firstMonthLabel(data);
  const quoted = periodQuotedValue(data);
  const gross = periodGrossQuoted(data);
  const quotedMult = quotedRevenueMultiple(data);
  const dedupPct = dedupCorrectionPct(data);
  const grossMult = grossInflationMultiple(data);
  const fullCount = data.filter((d) => !d.isPartial).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-foreground text-base font-semibold">
            Revenue, contribution &amp; estimates — monthly
          </h2>
          <p className="text-muted-foreground text-sm">
            Whole company · {basisWord} basis · trailing {months.length} months
          </p>
        </div>
        <BasisToggle basis={basis} onChange={setBasis} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          label={`Latest month ${basisWord}`}
          value={formatGbpCompact(latestRev)}
          sub={firstLabel ? `${firstLabel} onward` : undefined}
        />
        <Kpi
          label="Contribution margin"
          value={`${Math.round(latestMargin)}%`}
          sub={
            firstLabel
              ? `was ${Math.round(firstMargin)}% (${firstLabel})`
              : undefined
          }
          tone="good"
        />
        <Kpi
          label="Quoted value"
          value={formatGbpCompact(quoted)}
          sub={`deduped · ${quotedMult.toFixed(1)}× revenue`}
        />
        <Kpi
          label="Dedup correction"
          value={`−${Math.round(dedupPct)}%`}
          sub={`gross ${formatGbpCompact(gross)} → ${formatGbpCompact(quoted)}`}
          tone="warn"
        />
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend basisWord={basisWord} />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            {/* Explicit numeric [0, max] domains + a shared tick count keep
                both axes 0-floored and their gridlines aligned — see
                DailyRevenueChart and niceAxisMax. domain={[0, "auto"]} lets
                recharts re-centre the domain to align the two axes and push the
                left one below zero, reading as fake negative revenue.
                allowDataOverflow pins the floor to exactly 0 rather than
                letting a stray negative value expand it. */}
            <YAxis
              yAxisId="revenue"
              domain={[0, revenueMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <YAxis
              yAxisId="quoting"
              orientation="right"
              domain={[0, quotingMax]}
              allowDataOverflow
              tickCount={5}
              tickFormatter={formatGbpCompact}
              tick={{ fontSize: 10, fill: QUOTED_COLOR }}
              axisLine={false}
              tickLine={false}
              width={52}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={MonthlyTooltip as never}
            />
            <Bar
              yAxisId="revenue"
              dataKey="revenue"
              name="Revenue"
              fill={REVENUE_COLOR}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Bar
              yAxisId="revenue"
              dataKey="contribution"
              name="Contribution"
              fill={CONTRIBUTION_COLOR}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Line
              yAxisId="quoting"
              dataKey="grossLine"
              name="Gross quoted"
              stroke={GROSS_COLOR}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Line
              yAxisId="quoting"
              dataKey="quotedLine"
              name="Quoted value"
              stroke={QUOTED_COLOR}
              strokeWidth={2.4}
              dot={{ r: 2.6, fill: QUOTED_COLOR, strokeWidth: 0 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-muted-foreground mt-3 text-xs">
          <span className="text-foreground font-medium">Quoted value</span>{" "}
          (solid amber, right axis) — deduped to one row per enquiry, all
          outcomes — runs about {quotedMult.toFixed(1)}× {basisWord} revenue, a
          normal quote-to-win funnel. The dashed line is the pre-dedupe gross,
          inflated ~{grossMult.toFixed(1)}× by counting every revision. Margin
          remains the headline: {Math.round(firstMargin)}% →{" "}
          {Math.round(latestMargin)}%.
        </p>
        <p className="text-muted-foreground mt-2 text-xs">
          Revenue/contribution ={" "}
          {basis === "booked"
            ? "booked amounts by order date"
            : "invoiced amounts by invoice date"}
          . Quoted value = one row per estimate header ref (all outcomes),
          deduped within a system; gross = raw sum over every revision.
          Estimates span the LIVE + BCQ + POLLARDS eras. The final month is
          month-to-date (faded), excluded from the lines and the totals ·{" "}
          {fullCount} full months.
        </p>
      </div>
    </div>
  );
}

function BasisToggle({
  basis,
  onChange,
}: {
  basis: RevenueBasis;
  onChange: (b: RevenueBasis) => void;
}) {
  return (
    <div
      className="bg-muted inline-flex rounded-lg p-[3px]"
      role="group"
      aria-label="Revenue basis"
    >
      {(["booked", "invoiced"] as const).map((b) => (
        <button
          key={b}
          type="button"
          aria-pressed={basis === b}
          onClick={() => onChange(b)}
          className={
            "rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors " +
            (basis === b
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground")
          }
        >
          {b}
        </button>
      ))}
    </div>
  );
}

function ChartLegend({ basisWord }: { basisWord: string }) {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      <LegendSwatch color={REVENUE_COLOR} label={`Revenue (${basisWord})`} />
      <LegendSwatch color={CONTRIBUTION_COLOR} label="Contribution" />
      <LegendLine
        color={QUOTED_COLOR}
        label="Quoted value — deduped (right axis)"
      />
      <LegendLine
        color={GROSS_COLOR}
        label="Gross, pre-dedupe (right axis)"
        dashed
      />
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function LegendLine({
  color,
  label,
  dashed,
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-0 w-4"
        style={{
          borderTop: `${dashed ? "2px dashed" : "3px solid"} ${color}`,
        }}
      />
      {label}
    </span>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  className,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "good" | "warn";
  className?: string;
}) {
  const toneClass =
    tone === "good"
      ? "text-[var(--color-chart-2)]"
      : tone === "warn"
        ? "text-[#b45309]"
        : "text-foreground";
  return (
    <div
      className={
        "border-border bg-surface rounded-lg border p-3 " + (className ?? "")
      }
    >
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        className={`mt-1.5 font-mono text-xl font-semibold tabular-nums ${toneClass}`}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div>
      ) : null}
    </div>
  );
}

function MonthlyTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as MonthlyRevenueDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">
        {datum.label}
        {datum.isPartial ? (
          <span className="text-muted-foreground ml-1">· MTD</span>
        ) : null}
      </div>
      <TooltipRow
        color={REVENUE_COLOR}
        label="Revenue"
        value={formatGbpCompact(datum.revenue)}
      />
      <TooltipRow
        color={CONTRIBUTION_COLOR}
        label="Contribution"
        value={formatGbpCompact(datum.contribution)}
      />
      <div className="text-muted-foreground mt-0.5 mb-1">
        Margin {datum.marginPct.toFixed(1)}%
      </div>
      <TooltipRow
        color={QUOTED_COLOR}
        label="Quoted value"
        value={formatGbpCompact(datum.quotedValue)}
      />
      <TooltipRow
        color={GROSS_COLOR}
        label="Gross (pre-dedupe)"
        value={formatGbpCompact(datum.grossQuoted)}
      />
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
